/**
 * Packaging OS — Preview / Request access.
 *
 * Implements request-access-preview-spec.md §2.
 * Equal-tier "What you can do" + "What's locked" sections.
 * No backend write route in this phase — this surface is informational.
 */

import { Footer } from "../components/Footer";
import { LockedCard } from "../components/LockedCard";
import { PublicHeader } from "../components/PublicHeader";
import { TrustBanner } from "../components/TrustBanner";
import { Button } from "../components/Button";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

const ADMIN_EMAIL = "khorramymehr@gmail.com";

export function PreviewPage(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);

  function handleContactAdmin(): void {
    const subject = "Packaging OS — request access";
    window.location.href = `mailto:${ADMIN_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}`;
  }

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[{ to: "/", label: t.ctaBackToLanding }]}
      />

      <main className="po-page-main po-page-main--landing">
        <TrustBanner message={t.trustBannerFull} />

        <section
          aria-labelledby="po-preview-title"
          className="po-stack"
          style={{ alignItems: "flex-start" }}
        >
          <header className="po-section-header">
            <h1
              id="po-preview-title"
              className="po-section-header__title"
            >
              {t.previewTitle}
            </h1>
          </header>
          <p className="po-note">{t.previewBanner}</p>
          <p className="po-note po-note--muted po-note--small">
            {t.previewRequestAccessNotice}
          </p>
        </section>

        <section aria-labelledby="po-can-title">
          <header className="po-section-header">
            <h2
              id="po-can-title"
              className="po-section-header__title"
            >
              {t.previewYouCanHeading}
            </h2>
          </header>
          <ul className="po-list">
            <li>{t.previewYouCanItem1}</li>
            <li>{t.previewYouCanItem2}</li>
          </ul>
        </section>

        <section aria-labelledby="po-locked-title">
          <header className="po-section-header">
            <h2
              id="po-locked-title"
              className="po-section-header__title"
            >
              {t.previewLockedHeading}
            </h2>
          </header>
          <div className="po-grid po-grid--2">
            <LockedCard
              title={t.appShellComingSoonAudit}
              reason={t.previewLockedAuditReason}
            />
            <LockedCard
              title={t.previewLockedReviewReason.split(" — ")[0]}
              reason={t.previewLockedReviewReason}
            />
            <LockedCard
              title={t.previewLockedExportReason.split(" — ")[0]}
              reason={t.previewLockedExportReason}
            />
            <LockedCard
              title={t.previewLockedSupplierReason.split(" — ")[0]}
              reason={t.previewLockedSupplierReason}
            />
          </div>
        </section>

        <section aria-labelledby="po-contact-title" className="po-stack">
          <header className="po-section-header">
            <h2
              id="po-contact-title"
              className="po-section-header__title"
            >
              {t.previewContactAdminLead}
            </h2>
          </header>
          <div className="po-row">
            <Button variant="primary" onClick={handleContactAdmin}>
              {t.ctaRequestAccess}
            </Button>
          </div>
          <p className="po-note po-note--muted po-note--small dir-ltr-island">
            {ADMIN_EMAIL}
          </p>
        </section>
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
